import React from 'react';
import classnames from 'classnames';

import type { Repub } from '@cx-modules/constants';

import MiniBetslip from '~/components/betslip/components/mini-betslip';
import Icon from '~/components/icon';
import Logo from '~/components/Logo';
import MiniMyOC from '~/components/myoddschecker/components/mini-myoc';
import useCloseMenu from '~/hooks/use-close-menu';
import { useModalContext } from '~/providers/modal-context';
import { useMyOCContext } from '~/providers/myoc-context';
import { useLoaderContext } from '~/providers/loader-context';

import type INavigationCategory from '../../../../interfaces/navigation-category';
import type INavigationLink from '../../../../interfaces/navigation-link';
import useStore from '../../../../store';
import OCButton from '../../../button';
import Link from '../../../link';
import Menu from '../../../menu';
import StateDropdown from '../state-dropdown';

import AllSportsMenu from './sub-components/all-sports-menu';
import FeaturedSports from './sub-components/featured-sports';
import OddsFormatDropdown from './sub-components/odds-format-dropdown';
import Pages from './sub-components/pages';

import styles from './desktop-header.module.css';
import { useServicesContext } from '~/providers/services-context.tsx';

interface IProps {
  allSports: INavigationCategory[];
  featuredMenu: {pages: INavigationLink[];sports: INavigationLink[];};
  featuredSports: INavigationLink[];
  pages: INavigationLink[];
  repub: Repub;
  resourcesUrl: string;
  subMenu: INavigationLink[];
}

const DesktopHeader: React.FC<IProps> = ({
  allSports,
  featuredMenu,
  featuredSports,
  pages,
  resourcesUrl,
  subMenu
}) => {
  const [{ showMyOC }] = useModalContext();
  const { myocData } = useMyOCContext();
  const loaderData = useLoaderContext();
  const { labelActions, countryCode } = useServicesContext();

  const [showAllSports, setShowAllSports] = useStore((state) => [
  state.showAllSports,
  state.setShowAllSports]
  );

  const toggleAllSports = () => {
    setShowAllSports(!showAllSports);
  };

  const wrapperRef: React.MutableRefObject<any> = React.useRef(null);
  const isMenuOpen = useStore((state) => state.showAllSports);
  const hideAllSports = (): void => setShowAllSports(false);

  // Handle menu close on click or escape
  useCloseMenu(wrapperRef, isMenuOpen, hideAllSports);

  // Temporarily disable body scroll
  React.useEffect(() => {
    const body = document.querySelector('body');
    if (!body) return;

    const originalOverflow = body.style.overflow;

    if (showAllSports) {
      body.style.overflow = 'hidden';
    } else {
      body.style.overflow = originalOverflow;
    }

    return () => {
      body.style.overflow = originalOverflow;
    };
  }, [showAllSports]);

  return (
    <div
      data-testid="desktop-header"
      ref={wrapperRef}
      className={styles.desktopHeader}>

			<div className={styles.pagesRow}>
				<div className={styles.contents}>
					<Pages pages={pages} />
					<div className={styles.rightModule}>
						<OddsFormatDropdown />
						<StateDropdown />
						{showMyOC &&
            <MiniMyOC
              loaderData={loaderData}
              myocData={myocData}
              type="desktop" />

            }
						<MiniBetslip
              countryCode={countryCode}
              betslipTitle={labelActions.get('RESPONSIVE_BETSLIP')} />

					</div>
				</div>
			</div>
			<div className={styles.sportsRow}>
				<div className={styles.contents}>
					<Link to="" title="Oddschecker">
						<div className={styles.logoWrapper}>
							<Logo resourcesUrl={resourcesUrl} />
						</div>
					</Link>
					<OCButton
            onClick={toggleAllSports}
            className={classnames(
              styles.allSportsBtn,
              showAllSports ? styles.active : ''
            )}>

						<Icon icon="HamburgerMenu" /> {labelActions.get('RESPONSIVE_ODDS')}
					</OCButton>
					<FeaturedSports featuredSports={featuredSports} />
				</div>
			</div>
			{showAllSports &&
      <div className={styles.allSportsMenu}>
					<div className={styles.contents}>
						<AllSportsMenu
            allSports={allSports}
            featuredSports={featuredMenu.sports}
            featuredPages={featuredMenu.pages}
            closeMenuFn={toggleAllSports} />

					</div>
				</div>
      }{' '}
			{subMenu &&
      <div className={styles.subMenu}>
					<div className={styles.contents}>
						{/* eslint-disable-next-line */}
						{/* @ts-ignore */}
						<Menu customId="headerSubMenu" menu={subMenu} />
					</div>
				</div>
      }
		</div>);

};

export default DesktopHeader;